/*global chrome*/
import { api, config } from '../config';
import { ApiError, ParamsNotFoundError } from './definedErrors';
import UnsupportedDevice from '../components/UnsupportedDevice';
import UnsupportedBrowser from '../components/UnsupportedBrowser';
import DownloadExtension from '../components/DownloadExtension';
import Agreements from '../components/Agreements';
import ShareScreen from '../components/ShareScreen';
import ShareMicrophone from '../components/ShareMicrophone';
import ShareWebcam from '../components/ShareWebcam';
import WelcomeScreen from '../components/WelcomeScreen';
import SpeakerCheck from '../components/SpeakerCheck';
import RoomScan from '../components/RoomScan';
import Rules from '../components/Rules';
import IdCheck from '../components/IdCheck';
import SelfiePhoto from '../components/SelfiePhoto';
import WaitingForProctor from '../components/WaitingForProctor';
import ConnectedToProctor from '../components/ConnectedToProctor';
import SessionCompleted from '../components/SessionCompleted';
import SessionSurvey from '../components/SessionSurvey';
import ExamRedirect from '../components/ExamRedirect';
import ReconnectIntro from '../components/ReconnectIntro';
import ExamInProgress from '../components/ExamInProgress';
import SecondCameraIntroduction from '../components/SecondCamera/Enrollment/Introduction';
import SecondCameraView from '../components/SecondCamera/Enrollment/Camera';
import React from 'react';
import ExtensionNonResponsive from '../components/ExtensionNonResponsive';
import ReconnectCompleted from '../components/ReconnectCompleted';
import SecondCameraConnection from '../components/SecondCamera/Connection';
import SecondCameraPlacement from '../components/SecondCamera/Placement';

// Simple tab coordination helper
export const registerCurrentTab = () => {
  const tabId = crypto.randomUUID();
  const timestamp = Date.now();

  const tabInfo = { tabId, timestamp };

  // Store this tab's info in sessionStorage
  sessionStorage.setItem('currentTabInfo', JSON.stringify(tabInfo));

  // Update localStorage with the latest tab
  localStorage.setItem('latestOnboardingTab', JSON.stringify(tabInfo));

  return tabId;
};

export const isCurrentTabActive = () => {
  const currentTabInfo = JSON.parse(sessionStorage.getItem('currentTabInfo') || '{}');
  const latestTabInfo = JSON.parse(localStorage.getItem('latestOnboardingTab') || '{}');

  // Check if this tab is still the latest one
  return currentTabInfo.tabId === latestTabInfo.tabId;
};

export const sendMessageToExtension = (messageObject) => {
  return new Promise((resolve) => {
    try {
      if (chrome.runtime && window.chrome) {
        chrome.runtime.sendMessage(
          config.session.spExtensionId,
          messageObject,
          (response) => {
            if (chrome.runtime.lastError) {
              console.log('Chrome runtime lastError was hit');
              resolve(null);
            } else {
              resolve(response);
            }
          }
        );
      } else {
        resolve(null);
      }
    } catch (error) {
      console.log('Error encountered while messaging the extension', { error });
      resolve(null);
    }
  });
};

export function checkValidMachineSetup(window) {
  // First, we need to make sure that they are not on mobile and using a valid OS
  const os = getOperatingSystem(window);
  const result = {
    valid: true,
    id: null,
  };
  if (os === 'Unsupported') {
    result.valid = false;
    result.id = 'UnsupportedDevice';
  } else {
    // The OS is supported, so make sure we are on chrome
    const isChrome = checkIsOnChrome();
    if (!isChrome) {
      // Not on chrome
      result.valid = false;
      result.id = 'UnsupportedBrowser';
    }
  }
  // TODO: send the result of what OS they are using
  return result;
}

/**
 * Detects the operating system from the browser's user agent string
 *
 * @param {Window} window - The window object containing navigator information
 * @returns {string} The detected operating system: 'Windows', 'Mac', 'Chrome', 'Unsupported', or 'Not known'
 *
 * @example
 * const os = getOperatingSystem(window);
 * console.log(os); // 'Windows', 'Mac', etc.
 */
export function getOperatingSystem(window) {
  let operatingSystem = 'Not known';
  if (window.navigator.appVersion.indexOf('Win') !== -1) {
    operatingSystem = 'Windows';
  } else if (window.navigator.appVersion.indexOf('Mac') !== -1) {
    operatingSystem = 'Mac';
  } else if (window.navigator.appVersion.indexOf('Chrome') !== -1) {
    operatingSystem = 'Chrome';
  } else {
    operatingSystem = 'Unsupported';
  }

  return operatingSystem;
}

export function generateLinkCode(length = 8) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789' // No O, I, 0, 1
  let result = ''
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length)
    result += chars[randomIndex]
  }
  return result
}

export function parseMediaDeviceError(error) {
  let result = {
    code: error && error.name ? error.name : "unknownError",
    reason: ""
  };

  switch (result.code) {
    case "NotAllowedError":
      result.code = "notAllowedError";
      result.reason = "User denied permission or OS privacy settings blocked access.";
      break;
    case "NotFoundError":
      result.code = "notFoundError";
      result.reason = "No matching input/output devices were found (e.g., no microphone/camera).";
      break;
    case "NotReadableError":
      result.code = "notReadableError";
      result.reason = "The device exists but could not be accessed (possibly in use by another app or restricted by the OS).";
      break;
    case "OverconstrainedError":
      result.code = "overconstrainedError";
      result.reason = "The requested device constraints could not be satisfied by available hardware.";
      break;
    case "SecurityError":
      result.code = "securityError";
      result.reason = "Access is restricted due to security settings or context (e.g., insecure HTTP).";
      break;
    case "AbortError":
      result.code = "abortError";
      result.reason = "The operation was aborted for unknown reasons.";
      break;
    default:
      result.reason = error && error.message ? error.message : "An unknown error occurred.";
      break;
  }

  return result;
}

export function checkIsOnChrome() {
  // The code below needs to be as specific as it is so that we don't allow browsers like
  // Edge or Opera that are chromium based. We only want google chrome users to make
  // it through onboarding
  let isChrome;
  const isChromium = window.chrome;
  const vendorName = navigator.vendor;
  const isOpera = typeof window.opr !== 'undefined';
  let isIEedge = false;
  try {
    isIEedge = navigator.userAgentData.brands.some((brand) => {
      return brand.brand === 'Microsoft Edge';
    });
  } catch (error) {
    // No op as this does not exist in some browsers
  }

  if (
    isChromium !== null &&
    typeof isChromium !== 'undefined' &&
    vendorName === 'Google Inc.' &&
    isOpera === false &&
    isIEedge === false
  ) {
    isChrome = true;
  } else {
    isChrome = false;
  }
  // TODO: send info to API about browser (version number, etc)

  return isChrome;
}

export const renderStepComponent = (
  step,
  setIsTaskComplete,
  handleStep,
  setStepMetadata,
  inSession
) => {

  switch (step.id) {
    case 'UnsupportedDevice':
      return <UnsupportedDevice stepData={step?.stepData} />;
    case 'UnsupportedBrowser':
      return <UnsupportedBrowser stepData={step?.stepData} />;
    case 'DownloadExtension':
      return (
        <DownloadExtension stepData={step?.stepData} handleStep={handleStep} />
      );
    case 'ReconnectIntro':
      return (
        <ReconnectIntro
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    case 'ReconnectIntroInSession':
      return (
        <ReconnectIntro
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    case 'ReconnectInSessionCompleted':
      return (
        <ReconnectCompleted
          journeyStepId={step?.journeyStepId}
          stepData={step?.stepData}
          isInSession={inSession}
        />
      )
    case 'Agreements':
      return (
        <Agreements
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    case 'ShareScreen':
      return (
        <ShareScreen
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
          setStepMetadata={setStepMetadata}
          inSession={inSession}
        />
      );
    case 'ShareMicrophone':
      return (
        <ShareMicrophone
          stepData={step?.stepData}
          setStepMetadata={setStepMetadata}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    case 'ShareWebcam':
      return (
        <ShareWebcam
          stepData={step?.stepData}
          setStepMetadata={setStepMetadata}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    case 'WelcomeScreen':
      return (
        <WelcomeScreen
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    case 'SpeakerCheck':
      return (
        <SpeakerCheck
          stepData={step?.stepData}
          setStepMetadata={setStepMetadata}
          setIsTaskComplete={setIsTaskComplete}
        />
      );

    case 'RoomScan':
      return (
        <RoomScan
          stepData={step?.stepData}
          setStepMetadata={setStepMetadata}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    case 'Rules':
      return (
        <Rules
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    case 'IdCheck':
      return (
        <IdCheck
          stepData={step?.stepData}
          setStepMetadata={setStepMetadata}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    case 'SelfiePhoto':
      return (
        <SelfiePhoto
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    case 'WaitingForProctor':
      return (
        <WaitingForProctor
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    case 'ConnectedToProctor':
      return (
        <ConnectedToProctor
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    case 'ExamRedirect':
      return <ExamRedirect step={step} />;
    case 'SessionSurvey':
      return (
        <SessionSurvey
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    case 'ExtensionNonResponsive':
      return <ExtensionNonResponsive step={step} />;
    case 'SessionCompleted':
      return (
        <SessionCompleted
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
          step={step}
        />

    );
    case 'ExamInProgress':
      return <ExamInProgress step={step} inSession={inSession} />;

    case 'SecondCameraConnection':
      return (
        <SecondCameraConnection
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    case 'SecondCameraView':
      return (
        <SecondCameraView
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
        />
      );

    case 'SecondCameraIntroduction':
      return (
        <SecondCameraIntroduction
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
        />
      );

    case 'SecondCameraPlacement':
      return (
        <SecondCameraPlacement
          stepData={step?.stepData}
          setIsTaskComplete={setIsTaskComplete}
        />
      );
    default:
      return <div>Step Error - No Step Found</div>;
  }
};

export async function checkExtension() {
  const messageObject = {
    message: 'sp_background__checkExtension',
  };
  const result = await sendMessageToExtension(messageObject);
  return !!(result && result?.installation === 'SATISFIED');
}

export async function checkPermissions() {
  const messageObject = {
    message: 'sp_background__checkPermissions',
  };
  const result = await sendMessageToExtension(messageObject);
  return !!result;
}

export async function enablePermissions() {
  const messageObject = {
    message: 'sp_background__enablePermissions',
  };
  const result = await sendMessageToExtension(messageObject);
  return !!result;
}

export async function applyModalityOptions(modalityOptions) {
  const messageObject = {
    message: 'sp_background__applyModalityOptions',
    modalityOptions,
  };
  const result = await sendMessageToExtension(messageObject);
  return !!result;
}

export async function removeModalityOptions(modalityOptions) {
  const messageObject = {
    message: 'sp_background__removeModalityOptions',
    modalityOptions,
  };
  const result = await sendMessageToExtension(messageObject);
  return !!result;
}

export async function startBatteryMonitor(batteryConfig) {
  const messageObject = {
    message: 'sp_background__startBatteryMonitor',
    config: batteryConfig,
  };
  const result = await sendMessageToExtension(messageObject);
  return !!result;
}

export async function pinAndFocusTab(sessionStart, params = null) {
  const messageObject = {
    message: 'sp_background__pinAndFocusTab',
    sessionStart,
  };
  if (params) {
    messageObject.params = {
      ...params,
      apiUrl: config.proctoring.api.baseUrl,
      sessionUiUrl: config.session.sessionUiUrl,
    };
  }
  const result = await sendMessageToExtension(messageObject);
  return !!result;
}

export async function isTrackPublished(trackName) {
  const messageObject = {
    message: 'sp_background__isTrackPublished',
    trackName,
  };
  const result = await sendMessageToExtension(messageObject);
  return !!result;
}

export async function getActiveSessionFromExtension() {
  const messageObject = {
    message: 'sp_background__getActiveSession',
  };
  return await sendMessageToExtension(messageObject);
}

export async function disengageExtension() {
  const messageObject = {
    message: 'sp_background__disengage',
  };
  return await sendMessageToExtension(messageObject);
}

export async function getExtensionState(key) {
  const messageObject = {
    message: 'sp_background__getState',
    key,
  };
  const response = await sendMessageToExtension(messageObject);
  if (response && response.value) {
    return response.value;
  } else {
    return null;
  }
}

export async function dismissToolbarAlert(alertId) {
  const messageObject = {
    message: 'sp_background__dismissToolbarAlert',
    alertId,
  };
  const result = await sendMessageToExtension(messageObject);
  return !!result;
}

export async function toolbarButtonPressed(buttonId, runConfig = {}) {
  const messageObject = {
    message: 'sp_background__toolbarButtonPressed',
    runConfig,
    buttonId,
  };

  const result = await sendMessageToExtension(messageObject);
  return !!result;
}

export async function updateToolbarButtonState(buttonNames, enabled) {
  const messageObject = {
    message: 'sp_background__updateToolbarButtonState',
    buttonNames,
    enabled,
  };
  return await sendMessageToExtension(messageObject);
}

export async function raiseToolbar(source, toolbarId, height = '75px') {
  const messageObject = {
    message: 'sp_background__raiseToolbar',
    config: {
      source,
      height,
    },
    toolbarId,
  };
  const result = await sendMessageToExtension(messageObject);
  return !!result;
}

export async function confirmEndSession() {
  const messageObject = {
    message: 'sp_background__raiseConfirmation',
    iframeId: 'spEndSessionConfirmationId',
    bodyText:
      'End the proctoring session? Click "Confirm" to end.',
    confirmationFunctionName: 'endSession',
  };
  return await sendMessageToExtension(messageObject);
}

export async function focusExamTab() {
  const messageObject = {
    message: 'sp_background__focusExamTab'
  };
  return await sendMessageToExtension(messageObject);
}

export async function removeChat() {
  const messageObject = {
    message: 'sp_background__removeChat',
    iframeId: 'spProctorChatIframeId',
  };
  return await sendMessageToExtension(messageObject);
}

export async function removeSupportChat() {
  const messageObject = {
    message: 'sp_background__removeSupportChat',
    iframeId: 'spSupportChatIframeId',
  };
  return await sendMessageToExtension(messageObject);
}

export async function removeSessionInfo() {
  const messageObject = {
    message: 'sp_background__removeSessionInfo',
    iframeId: 'spSessionInfoIframeId',
  };
  return await sendMessageToExtension(messageObject);
}

export async function removeExamPassword() {
  const messageObject = {
    message: 'sp_background__removeExamPassword',
    iframeId: 'spExamPasswordIframeId',
  };
  return await sendMessageToExtension(messageObject);
}

export async function removeLiveView() {
  const messageObject = {
    message: 'sp_background__removeLiveView',
    iframeId: 'spLiveViewIframeId',
  };
  return await sendMessageToExtension(messageObject);
}

export async function removeCalculator() {
  const messageObject = {
    message: 'sp_background__removeCalculator',
    iframeId: 'spCalculatorIframeId',
  };
  return await sendMessageToExtension(messageObject);
}

export async function updateCalculatorState(state) {
  const messageObject = {
    message: 'sp_background__updateCalculatorState',
    iframeId: 'spCalculatorIframeId',
    state,
  };
  return await sendMessageToExtension(messageObject);
}

export async function removeIframe(iframeId) {
  const messageObject = {
    message: 'sp_background__removeIframe',
    iframeId,
  };
  return await sendMessageToExtension(messageObject);
}

export const updateIframe = async (
  iframeId,
  dimensions,
  persist = false,
  iframeType = null
) => {
  const messageObject = {
    message: 'sp_background__updateIframe',
    iframeId,
    dimensions,
    persist,
    iframeType,
  };
  const result = await sendMessageToExtension(messageObject);
  return !!result;
};

export async function uninstallExtension() {
  try{
    const messageObject = {
      message: 'sp_background__uninstallSelf',
      withConfirmation: false,
    };
    const result = await sendMessageToExtension(messageObject);
    // null will be returned if it was uninstalled successfully
    if(result === null){
      return true;
    }
    return false;
  }catch(error){
    return false;
  }
}

export async function collapseIframe(iframeId) {
  const messageObject = {
    message: 'sp_background__collapseIframe',
    iframeId,
  };
  return await sendMessageToExtension(messageObject);
}

export async function expandIframe(iframeId) {
  const messageObject = {
    message: 'sp_background__expandIframe',
    iframeId,
  };
  return await sendMessageToExtension(messageObject);
}

export async function endSessionProctor(
  bodyText = 'Your session was forced to end',
  reasonText = 'No proctors are available at the scheduled institution'
) {
  const messageObject = {
    message: 'sp_background__endSessionProctor',
    bodyText,
    reasonText,
  };
  return await sendMessageToExtension(messageObject);
}

export async function createAndJoinMediaRoom(identity, websocketUrl, status, inSessionLockoutPeriod) {
  const messageObject = {
    message: 'sp_background__createMediaRoom',
    identity,
    websocketUrl,
    status,
    inSessionLockoutPeriod,
  };
  const result = await sendMessageToExtension(messageObject);
  return !!result;
}

export const requestInformationalStep = async (stepId, params = null) => {
  if (!params) {
    params = JSON.parse(sessionStorage.getItem('spParams'));
    if (!params) {
      console.error('No params were found!');
      throw new ParamsNotFoundError();
    }
  }
  try {
    const url = `/v1/installs/${params.installSid}/exams/${params.examSid}/sessions/${params.sessionSid}/step`;
    const headers = {
      token: params.token,
    };
    const payload = {
      stepId,
    };
    const response = await api.put(url, headers, payload);
    return response.data;
  } catch (error) {
    console.error('Error requesting informational step:', {
      error,
      stepId,
      params,
    });
    throw new ApiError();
  }
};

export const getSessionFromOnboarding = async (params = null) => {
  if (!params) {
    params = JSON.parse(sessionStorage.getItem('spParams'));
    if (!params) {
      console.error('No params were found!');
      throw new ParamsNotFoundError();
    }
  }
  try {
    const url = `/v1/installs/${params.installSid}/exams/${params.examSid}/sessions/${params.sessionSid}`;
    const headers = {
      token: params.token,
    };
    const response = await api.get(url, headers);
    return response.data;
  } catch (error) {
    console.error('Error getting session from onboarding:', error);
    throw new ApiError();
  }
};

export const getMediaStart = async (userSid, token) => {
  try {
    const url = `/v1/media/users/${userSid}/start`;
    const headers = {
      token,
    };
    const response = await api.get(url, headers);
    return response.data;
  } catch (error) {
    console.error('Error getting media start data:', error);
    throw new ApiError();
  }
};

export const listAttachments = async (params = null) => {
  if (!params) {
    params = JSON.parse(sessionStorage.getItem('spParams'));
    if (!params) {
      console.error('No params were found!');
      throw new ParamsNotFoundError();
    }
  }
  try {
    const url = `/v1/installs/${params.installSid}/exams/${params.examSid}/sessions/${params.sessionSid}/attachments`;
    const headers = {
      token: params.token,
    };
    const response = await api.get(url, headers);
    return response.data?.results || [];
  } catch (error) {
    console.error('Error getting attachments:', error);
    throw new ApiError();
  }
};

export const getAttachment = async (attachmentSid, params = null) => {
  if (!params) {
    params = JSON.parse(sessionStorage.getItem('spParams'));
    if (!params) {
      console.error('No params were found!');
      throw new ParamsNotFoundError();
    }
  }

  try {
    const url = `/v1/installs/${params.installSid}/exams/${params.examSid}/sessions/${params.sessionSid}/attachments/${attachmentSid}?redirect=false`;
    const headers = {
      token: params.token,
    };
    const response = await api.get(url, headers);
    return response.data?.url || null;
  } catch (error) {
    console.error('Error getting attachment:', error);
    throw new ApiError();
  }
};

export const removeAttachmentItem = async (iframeId) => {
  const messageObject = {
    message: 'sp_background__removeAttachmentItem',
    iframeId,
  };
  const result = await sendMessageToExtension(messageObject);
  return !!result;
};

export const raiseAttachmentItem = async (iframeId, title, sessionUiUrl) => {
  const messageObject = {
    message: 'sp_background__raiseAttachmentItem',
    iframeId,
    title,
    sessionUiUrl,
  };
  const result = await sendMessageToExtension(messageObject);
  return !!result;
};

export const getSessionSurveyData = async (params = null) => {
  if (!params) {
    params = JSON.parse(sessionStorage.getItem('spParams'));
    if (!params) {
      console.error('No params were found!');
      throw new ParamsNotFoundError();
    }
  }
  try {
    const url = `/v1/installs/${params.installSid}/exams/${params.examSid}/sessions/${params.sessionSid}/survey`;
    const headers = {
      token: params.token,
    };
    const response = await api.get(url, headers);
    return response.data;
  } catch (error) {
    // TODO: log to papertrail on these in all the catches
    console.error('Error getting survey data:', error);
    throw new ApiError();
  }
};

export const submitSurveyResponses = async (params = {}) => {
    const storedParams = JSON.parse(sessionStorage.getItem('spParams'));

    // if anything
    if(!params.installSid){
      params.installSid = storedParams.installSid;
    }
    if(!params.examSid){
      params.examSid = storedParams.examSid;
    }
    if(!params.sessionSid){
      params.sessionSid = storedParams.sessionSid;
    }
    if(!params.token){
      params.token = storedParams.token;
    }

  try {
    const url = `/v1/installs/${params.installSid}/exams/${params.examSid}/sessions/${params.sessionSid}/survey`;
    const headers = {
      token: params.token,
    };
    const response = await api.post(url, headers, params.payload);
    return response.data;
  } catch (error) {
    // TODO: log to papertrail on these in all the catches
    console.error('Error submitting survey data:', error);
    throw new ApiError();
  }
};

export const toHumanReadable = (camelCase) => {
  // Convert camelCase to Title Case with spaces
  return (
    camelCase
      // Add space before capital letters
      .replace(/([A-Z])/g, ' $1')
      // Capitalize first letter
      .replace(/^./, (str) => str.toUpperCase())
      .trim()
  );
};

/**
 * Converts a number to its ordinal word equivalent
 * @param {number} num - The number to convert (1-10)
 * @returns {string} The ordinal word (e.g., 'first', 'second')
 */
export const getOrdinal = (num) => {
  const ordinals = [
    'first', 'second', 'third', 'fourth', 'fifth',
    'sixth', 'seventh', 'eighth', 'ninth', 'tenth'
  ];
  return ordinals[num - 1] || `${num}th`;
};